import React, { useState } from 'react';
import { FileText, ChevronRight } from 'lucide-react';

interface HeroSectionProps {
  onRequestAccess: () => void;
  onSignIn: () => void;
}

type Claim = 'g' | 'i' | 'gap' | null;

/**
 * Landing hero. Left: the thesis headline + CTAs. Right: the interactive
 * evidence demo — hovering any highlighted claim (or its matching chip) tints
 * the claim, dramatising BidWin's signature "every assertion carries its
 * source" discipline. Pure local state; no data dependency.
 */
export function HeroSection({ onRequestAccess, onSignIn }: HeroSectionProps) {
  const [active, setActive] = useState<Claim>(null);

  const hover = (c: Claim) => ({
    onMouseEnter: () => setActive(c),
    onMouseLeave: () => setActive(null),
  });

  return (
    <section id="top" className="max-w-[1180px] mx-auto px-5 sm:px-8 pt-16 sm:pt-[88px] pb-16 sm:pb-[72px] scroll-mt-20">
      <div className="flex flex-wrap gap-12 lg:gap-16 items-center">
        {/* Thesis */}
        <div className="flex-1 basis-[440px] min-w-[300px]">
          <h1 className="text-[clamp(40px,5.4vw,68px)] leading-[1.02] font-extrabold tracking-[-0.035em] text-bw-sidebar mb-6">
            Win the bids worth winning. <span className="text-bw-primary">Prove</span> every{' '}
            <span className="relative inline-block">
              <span className="absolute inset-x-0 bottom-1 h-2.5 bg-bw-accent/20 rounded-sm" aria-hidden="true" />
              <span className="relative">claim</span>
            </span>
            .
          </h1>
          <p className="text-[clamp(17px,1.5vw,20px)] leading-[1.55] text-fit-weak max-w-[520px] mb-8">
            BidWin surfaces the federal opportunities that actually fit your business, scores them
            honestly, and drafts proposals grounded in your real evidence —{' '}
            <span className="text-bw-ink font-semibold">never invented.</span>
          </p>
          <div className="flex flex-wrap gap-3.5 mb-5">
            <button
              onClick={onRequestAccess}
              className="text-base font-semibold text-white bg-bw-primary px-7 py-3.5 rounded-[9px] shadow-card hover:brightness-110 transition"
            >
              Request access
            </button>
            <button
              onClick={onSignIn}
              className="text-base font-semibold text-bw-ink bg-bw-surface border border-bw-border px-7 py-3.5 rounded-[9px] inline-flex items-center gap-2 hover:bg-bw-surface-subtle transition"
            >
              Sign in
              <ChevronRight className="w-4 h-4 text-bw-primary" strokeWidth={2.5} />
            </button>
          </div>
          <div className="font-mono text-xs text-bw-muted tracking-wide">
            Discovery to submission in one place
          </div>
        </div>

        {/* Interactive evidence demo */}
        <div className="flex-1 basis-[420px] min-w-[300px]">
          <div className="bg-bw-surface border border-bw-border rounded-2xl shadow-panel overflow-hidden">
            {/* card header */}
            <div className="flex items-center justify-between px-[18px] py-3.5 border-b border-bw-surface-subtle bg-bw-surface-subtle/40">
              <div className="flex items-center gap-2">
                <FileText className="w-[15px] h-[15px] text-bw-primary" strokeWidth={2} />
                <span className="text-[12.5px] font-semibold text-bw-ink">Proposal · Technical Approach</span>
              </div>
              <span className="font-mono text-[10.5px] text-bw-muted tracking-[0.08em]">AI DRAFT</span>
            </div>

            {/* draft prose with hoverable claims */}
            <div className="px-5 pt-5 pb-2 text-[16.5px] leading-[1.7] text-bw-ink">
              Our team brings{' '}
              <span
                {...hover('g')}
                className={`border-b-2 border-evidence-grounded rounded-[3px] px-0.5 cursor-default transition-colors ${active === 'g' ? 'bg-evidence-grounded-bg' : ''}`}
              >
                five years of NAICS 541519 past performance
              </span>{' '}
              and{' '}
              <span
                {...hover('i')}
                className={`border-b-2 border-evidence-inferred-border rounded-[3px] px-0.5 cursor-default transition-colors ${active === 'i' ? 'bg-evidence-inferred-bg' : ''}`}
              >
                hands-on React modernization experience
              </span>
              , though we hold{' '}
              <span
                {...hover('gap')}
                className={`border-b-2 border-dashed border-evidence-gap-border rounded-[3px] px-0.5 cursor-default transition-colors ${active === 'gap' ? 'bg-evidence-gap-bg' : ''}`}
              >
                no prior Department of the Interior contracts
              </span>
              .
            </div>

            {/* evidence chip rows */}
            <div className="px-5 pt-4 pb-5 flex flex-col gap-2.5">
              <div
                {...hover('g')}
                style={{ animationDelay: '0.15s' }}
                className="animate-fade-in [animation-fill-mode:both] flex items-center gap-2.5 px-3 py-2.5 bg-evidence-grounded-bg border-l-[3px] border-evidence-grounded rounded-r-lg cursor-default"
              >
                <span className="text-evidence-grounded text-[13px]">●</span>
                <span className="text-[13px] font-semibold text-evidence-grounded">Cited</span>
                <span className="text-[13px] text-fit-weak">— traces to Past Performance log</span>
              </div>
              <div
                {...hover('i')}
                style={{ animationDelay: '0.35s' }}
                className="animate-fade-in [animation-fill-mode:both] flex items-center gap-2.5 px-3 py-2.5 bg-evidence-inferred-bg border-l-[3px] border-evidence-inferred-border rounded-r-lg cursor-default"
              >
                <span className="text-evidence-inferred-border text-[13px]">◐</span>
                <span className="text-[13px] font-semibold text-evidence-inferred">Inferred</span>
                <span className="text-[13px] text-fit-weak">— from “JavaScript frameworks” in capabilities</span>
              </div>
              <div
                {...hover('gap')}
                style={{ animationDelay: '0.55s' }}
                className="animate-fade-in [animation-fill-mode:both] flex items-center gap-2.5 px-3 py-2.5 bg-evidence-gap-bg border-l-[3px] border-dashed border-evidence-gap-border rounded-r-lg cursor-default"
              >
                <span className="text-evidence-gap text-[13px]">○</span>
                <span className="text-[13px] font-semibold text-evidence-gap">No evidence</span>
                <span className="text-[13px] text-evidence-gap">— flagged for you, never hidden</span>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-bw-surface-subtle font-mono text-[11px] text-bw-muted tracking-wide">
              Hover a claim — every assertion carries its source.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
